import { useCallback, useEffect, useState } from "react";
import { apiFetch, ApiError } from "@/lib/api-client";

export type CustomFieldEntityType = "person" | "company" | "deal";
export type CustomFieldType = "text" | "number" | "date" | "boolean" | "select";

export interface CustomFieldOption {
  value: string;
  label: string;
  color?: string;
}

export interface CustomFieldDefinition {
  id: string;
  entityType: CustomFieldEntityType;
  fieldKey: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  options: CustomFieldOption[] | null;
  required: boolean;
  showInList: boolean;
  showInCard: boolean;
  displayOrder: number;
  validation: Record<string, unknown> | null;
}

export interface UpsertCustomFieldInput {
  entityType: CustomFieldEntityType;
  fieldKey: string;
  fieldLabel: string;
  fieldType: CustomFieldType;
  options?: CustomFieldOption[];
  required?: boolean;
  showInList?: boolean;
  showInCard?: boolean;
  displayOrder?: number;
  validation?: Record<string, unknown>;
}

export function useCustomFields(entityType: CustomFieldEntityType) {
  const [fields, setFields] = useState<CustomFieldDefinition[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFields = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await apiFetch<{ data: CustomFieldDefinition[] }>(`/crm/custom-fields?entityType=${entityType}`);
      setFields(response.data);
    } catch (caughtError) {
      setFields([]);
      setError(caughtError instanceof ApiError ? caughtError.message : "Failed to load custom fields");
    } finally {
      setIsLoading(false);
    }
  }, [entityType]);

  useEffect(() => {
    void fetchFields();
  }, [fetchFields]);

  return { fields, isLoading, error, refetch: fetchFields, setFields };
}

export async function createCustomField(input: UpsertCustomFieldInput) {
  const response = await apiFetch<{ data: CustomFieldDefinition }>("/crm/custom-fields", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function updateCustomField(id: string, input: Partial<UpsertCustomFieldInput>) {
  const response = await apiFetch<{ data: CustomFieldDefinition }>(`/crm/custom-fields/${id}`, {
    method: "PATCH",
    body: JSON.stringify(input),
  });
  return response.data;
}

export async function deleteCustomField(id: string) {
  await apiFetch(`/crm/custom-fields/${id}`, { method: "DELETE" });
}
